import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Form,
  Space,
  Button,
  message,
  Spin,
  notification,
  Tag,
} from "antd";
import { FaPlus, FaEdit, FaSearch } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";
import CategoryModal from "./CategoryModal"; // Import the modal

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [uploadingLoading, setUploadingLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await apiHandle.get("admin/get-categories");
      setCategories(response?.data?.data);
    } catch (error) {
      message.error("Failed to fetch categories.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    setUploadingLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await apiHandle.post("s3-cloud-storage", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.files) {
        setUploadedImage(response?.data?.files?.url);
        form.setFieldsValue({ image: response?.data?.files?.url });
      } else {
        message.error("Image upload failed.");
      }
    } catch (error) {
      message.error("Image upload error.");
    } finally {
      setUploadingLoading(false);
    }
  };

  const handleAddOrUpdateCategory = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingCategory) {
        await apiHandle.post(`admin/edit-category/${editingCategory._id}`, {
          ...values,
          image: uploadedImage || editingCategory.image,
          description: values.description,
          parent_category: values.parent_category || null,
        });
        notification.success({ message: "Category updated successfully" });
      } else {
        await apiHandle.post("admin/add-category", {
          name: values.name,
          image: uploadedImage,
          description: values.description,
          parent_category: values.parent_category || null,
        });
        message.success("Category added successfully");
      }

      fetchCategories();
      form.resetFields();
      setUploadedImage("");
      setIsModalVisible(false);
      setEditingCategory(null);
    } catch (error) {
      notification.error({
        message: "Please fill in all required fields correctly.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({
      name: record.name,
      status: record.status,
      image: record.image,
      description: record.description || "",
      parent_category: record.parent_category?._id || null,
    });
    setUploadedImage(record.image);
    setIsModalVisible(true);
  };

  const handleRemoveImage = () => {
    setUploadedImage("");
    form.setFieldsValue({ image: "" });
  };

  const handleDescriptionChange = (content) => {
    form.setFieldsValue({ description: content });
  };

  const getAvailableParentCategories = () => {
    if (!editingCategory) return categories;

    const filterDescendants = (categoryId) => {
      const descendants = categories.filter(
        (cat) => cat.parent_category === categoryId
      );
      let allDescendants = [...descendants];
      descendants.forEach((desc) => {
        allDescendants = allDescendants.concat(filterDescendants(desc._id));
      });
      return allDescendants;
    };

    const descendants = filterDescendants(editingCategory._id);
    const excludedIds = [editingCategory._id, ...descendants.map((d) => d._id)];

    return categories.filter((cat) => !excludedIds.includes(cat._id));
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (record) => (
        <Space>
          <img
            src={
              record?.image ||
              "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"
            }
            alt="logo"
            style={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
          <span>{record?.name}</span>
        </Space>
      ),
    },
    {
      title: "Parent Collection",
      key: "parent_category",
      render: (record) => (
        <Space>
          <span>
            {record?.parent_category ? record?.parent_category?.name : "None"}
          </span>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space>
          <Button icon={<FaEdit />} onClick={() => handleEdit(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-4">
        Collection Management
      </h1>

      <div className="flex justify-between my-4">
        <Input
          placeholder="Search by name"
          prefix={<FaSearch />}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "300px" }}
        />
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => {
            form.resetFields();
            setUploadedImage("");
            setIsModalVisible(true);
          }}
        >
          Add Collection
        </Button>
      </div>

      {loading ? (
        <Spin size="large" className="flex justify-center" />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredCategories}
          pagination={{ pageSize: 15 }}
          rowKey="_id"
        />
      )}

      <CategoryModal
        isModalVisible={isModalVisible}
        handleCancel={() => {
          setIsModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
          setUploadedImage("");
        }}
        handleAddOrUpdateCategory={handleAddOrUpdateCategory}
        form={form}
        uploadedImage={uploadedImage}
        uploadingLoading={uploadingLoading}
        handleImageUpload={handleImageUpload}
        handleRemoveImage={handleRemoveImage}
        handleDescriptionChange={handleDescriptionChange}
        getAvailableParentCategories={getAvailableParentCategories}
        editingCategory={editingCategory}
        loading={loading}
      />
    </div>
  );
};

export default Category;
